import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Pencil, Trash2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, ApiError } from '@/lib/api-client';
import { useAuth } from '@/lib/auth-context';
import { invalidateGroups } from '@/lib/restaurant-groups';
import { Spinner, TableSkeleton } from '@/components/ui/spinner';
import { MultiSelect } from '@/components/ui/multi-select';

interface GroupRow {
  id: string;
  name: string;
  fb: string[];
  ig: string[];
  ga4: string[];
}

interface AccountsData {
  fb: string[];
  ig: string[];
  ga4: string[];
}

interface ApiPlatformError {
  detail?: string;
  title?: string;
  violations?: Array<{ propertyPath: string; message: string }>;
}

const formatError = (e: unknown, fallback: string): string => {
  if (e instanceof ApiError) {
    const body = (e.body ?? {}) as ApiPlatformError;
    if (body.violations && body.violations.length > 0) {
      return body.violations.map((v) => `${v.propertyPath}: ${v.message}`).join(', ');
    }
    if (body.detail) return body.detail;
    if (e.message) return e.message;
  }
  return fallback;
};

export default function SettingsRestaurantsPage() {
  const { isAdmin } = useAuth();
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [accounts, setAccounts] = useState<AccountsData>({ fb: [], ig: [], ga4: [] });
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formFb, setFormFb] = useState<string[]>([]);
  const [formIg, setFormIg] = useState<string[]>([]);
  const [formGa4, setFormGa4] = useState<string[]>([]);
  const [saveError, setSaveError] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const loadGroups = async () => {
    setGroupsLoading(true);
    try {
      const data = await apiClient.get<GroupRow[]>('/restaurant-groups');
      setGroups(data);
    } catch {
      // ignore — admin guard prevents unauthorized access
    } finally {
      setGroupsLoading(false);
    }
  };

  const loadAccounts = async () => {
    setAccountsLoading(true);
    try {
      const [fbData, igData, ga4Data] = await Promise.all([
        apiClient.get<Array<{ account_name: string }>>('/sheets/facebook'),
        apiClient.get<Array<{ account_name: string }>>('/sheets/instagram'),
        apiClient.get<Array<{ account_name: string }>>('/sheets/ga4'),
      ]);
      setAccounts({
        fb: [...new Set(fbData.map((d) => d.account_name).filter(Boolean))].sort(),
        ig: [...new Set(igData.map((d) => d.account_name).filter(Boolean))].sort(),
        ga4: [...new Set(ga4Data.map((d) => d.account_name).filter(Boolean))].sort(),
      });
    } catch {
      // ignore — accounts list will simply be empty
    } finally {
      setAccountsLoading(false);
    }
  };

  useEffect(() => {
    void loadGroups();
    void loadAccounts();
  }, []);

  if (!isAdmin) return <div className="p-8 text-center text-muted-foreground">Nemáte přístup.</div>;

  const openNew = () => {
    setEditId(null);
    setFormName('');
    setFormFb([]);
    setFormIg([]);
    setFormGa4([]);
    setSaveError('');
    setDialogOpen(true);
  };

  const openEdit = (g: GroupRow) => {
    setEditId(g.id);
    setFormName(g.name);
    setFormFb(g.fb);
    setFormIg(g.ig);
    setFormGa4(g.ga4);
    setSaveError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaveError('');
    const body = { name: formName, fb: formFb, ig: formIg, ga4: formGa4 };
    try {
      if (editId) {
        await apiClient.patch<GroupRow>(`/restaurant-groups/${editId}`, body);
        toast.success('Restaurace uložena');
      } else {
        await apiClient.post<GroupRow>('/restaurant-groups', body);
        toast.success('Restaurace vytvořena');
      }
      invalidateGroups();
      await loadGroups();
      setDialogOpen(false);
    } catch (e) {
      setSaveError(formatError(e, 'Chyba při ukládání'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Opravdu smazat tuto restauraci?')) return;
    setDeleteError('');
    try {
      await apiClient.delete<null>(`/restaurant-groups/${id}`);
      invalidateGroups();
      await loadGroups();
      toast.success('Restaurace smazána');
    } catch (e) {
      const msg = formatError(e, 'Chyba při mazání');
      setDeleteError(msg);
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Restaurace</h1>
          <p className="text-muted-foreground">Mapování FB + IG + GA4 účtů dohromady</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger
            onClick={openNew}
            className="inline-flex items-center gap-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 cursor-pointer"
          >
            <Plus className="h-4 w-4" />
            Nová restaurace
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editId ? 'Upravit restauraci' : 'Nová restaurace'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="space-y-2">
                <Label>Název</Label>
                <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Monte Bú" />
              </div>

              <AccountsField
                label="Facebook účty"
                colorClass="text-[#c9a84c]"
                badgeBorder="border-[#c9a84c]/40"
                available={accounts.fb}
                selected={formFb}
                loading={accountsLoading}
                onChange={setFormFb}
              />

              <AccountsField
                label="Instagram účty"
                colorClass="text-[#d4816b]"
                badgeBorder="border-[#d4816b]/40"
                available={accounts.ig}
                selected={formIg}
                loading={accountsLoading}
                onChange={setFormIg}
              />

              <AccountsField
                label="GA4 property"
                colorClass="text-[#7c9a5e]"
                badgeBorder="border-[#7c9a5e]/40"
                available={accounts.ga4}
                selected={formGa4}
                loading={accountsLoading}
                onChange={setFormGa4}
              />

              {saveError && <p className="text-sm text-destructive">{saveError}</p>}

              <Button onClick={handleSave} className="w-full">
                {editId ? 'Uložit změny' : 'Vytvořit restauraci'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" />
            Skupiny {groupsLoading ? <Spinner size="sm" /> : `(${groups.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {deleteError && <p className="text-sm text-destructive mb-2">{deleteError}</p>}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Název</TableHead>
                <TableHead>Facebook</TableHead>
                <TableHead>Instagram</TableHead>
                <TableHead>GA4</TableHead>
                <TableHead className="text-right">Akce</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groupsLoading && <TableSkeleton rows={6} cols={5} />}
              {!groupsLoading && groups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">
                    Žádné skupiny
                  </TableCell>
                </TableRow>
              )}
              {!groupsLoading && groups.map((g) => (
                <TableRow key={g.id}>
                  <TableCell className="font-medium">{g.name}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {g.fb.map((a) => (
                        <Badge key={a} variant="outline" className="text-[10px] border-[#c9a84c]/30">
                          {a}
                        </Badge>
                      ))}
                      {g.fb.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {g.ig.map((a) => (
                        <Badge key={a} variant="outline" className="text-[10px] border-[#d4816b]/30">
                          {a}
                        </Badge>
                      ))}
                      {g.ig.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {g.ga4.map((a) => (
                        <Badge key={a} variant="outline" className="text-[10px] border-[#7c9a5e]/30">
                          {a}
                        </Badge>
                      ))}
                      {g.ga4.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(g)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(g.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// --- AccountsField ---

interface AccountsFieldProps {
  label: string;
  colorClass: string;
  badgeBorder: string;
  /** Account names known from Windsor (Google Sheets). */
  available: string[];
  /** Currently selected account names for this group (may include custom not in `available`). */
  selected: string[];
  /** True while available list is being fetched. */
  loading?: boolean;
  onChange: (next: string[]) => void;
}

function AccountsField({ label, colorClass, badgeBorder, available, selected, loading, onChange }: AccountsFieldProps) {
  return (
    <div className="space-y-2">
      <Label className={colorClass}>{label}</Label>
      <MultiSelect
        options={available}
        value={selected}
        onChange={onChange}
        loading={loading}
        placeholder="Vyber účet z Windsoru…"
        emptyLabel="Žádné účty z Windsoru"
        badgeBorderClass={badgeBorder}
      />
    </div>
  );
}

